import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import * as pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import RotateLeft from '@mui/icons-material/RotateLeft';
import RotateRight from '@mui/icons-material/RotateRight';
import { useTranslation } from 'react-i18next';
import PreviewZoomComponent from 'features/document/edit/documents/components/PreviewZoomComponent';
import { Button, IconButton, Typography } from '@totalsoft/rocket-ui';
import { BoxActionsWrapper } from 'components/common/CommonStyles';
import { PdfViewerDialog } from '../BulkDocumentsStyles';
import { Grid } from '@mui/material';

export default function PdfPageViewer({ open, setOpen, preview, setPreview, pageIndex, pages, setPages }) {
  const { t } = useTranslation();

  const [scale, setScale] = useState(1.0);

  const canvasRef = useRef();
  const divRef = useRef();
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const [pdfRef, setPdfRef] = useState();
  const [currentPage, setCurrentPage] = useState(pageIndex);

  const rotatePage = rotateClockwise => {
    setPages(pages => {
      return pages.map(page => {
        if (page.item?.storageKey === preview.item.storageKey) {
          return {
            ...page,
            item: {
              ...page.item,
              rotationAngle: rotateClockwise ? (page.item.rotationAngle + 90) % 360 : (page.item.rotationAngle - 90 + 360) % 360
            }
          };
        }
        return page;
      });
    });
  };

  const getPreviewTitle = preview => {
    if (preview?.isDocument) return preview.attachment.name;
    else return `${preview?.item?.fileName} ${preview?.page !== null ? `- P${preview?.page + 1}` : ``}`;
  };

  const renderPage = useCallback(
    pageNum => {
      pdfRef?.getPage(pageNum).then(function (page) {
        const viewport = page.getViewport({
          scale,
          rotation: preview.canRotate ? pages.find(x => x.item?.storageKey === preview.item?.storageKey)?.item?.rotationAngle : 0
        });
        const canvas = canvasRef.current;
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const renderContext = {
          canvasContext: canvas.getContext('2d', { willReadFrequently: true }),
          viewport: viewport
        };
        page.render(renderContext);
      });
    },
    [pdfRef, scale, pages, preview]
  );

  const renderAllPages = useCallback(
    async (page, rotationAngle, pageNum) => {
      const existingWrapper = divRef?.current?.children[pageNum];

      if (existingWrapper) {
        const existingCanvas = existingWrapper.querySelector('canvas');
        if (existingCanvas) {
          const viewport = page.getViewport({
            scale,
            rotation: rotationAngle ?? 0
          });

          existingCanvas.height = viewport.height;
          existingCanvas.width = viewport.width;

          const renderContext = {
            canvasContext: existingCanvas.getContext('2d', { willReadFrequently: true }),
            viewport: viewport
          };

          await page.render(renderContext);
        }
      } else {
        const viewport = page.getViewport({
          scale,
          rotation: rotationAngle ?? 0
        });
        const wrapper = document.createElement('div');
        wrapper.style.marginBottom = '15px';
        const pageCanvas = document.createElement('canvas');
        pageCanvas.height = viewport.height;
        pageCanvas.width = viewport.width;
        const renderContext = {
          canvasContext: pageCanvas.getContext('2d', { willReadFrequently: true }),
          viewport: viewport
        };
        wrapper.appendChild(pageCanvas);
        divRef.current.appendChild(wrapper);

        await page.render(renderContext);
      }
    },
    [scale]
  );

  const renderPages = useCallback(() => {
    for (let num = 1; num <= pdfRef?.numPages; num++) pdfRef.getPage(num).then(page => renderAllPages(page, 0, num - 1));
  }, [renderAllPages, pdfRef]);

  const renderNewDocumentPages = useCallback(async () => {
    if (!pdfRef) return;

    for (let page in preview.pages) {
      if (pdfRef[preview.pages[page].item.correlationId]) {
        const pdfPage = await pdfRef[preview.pages[page].item.correlationId].getPage(preview.pages[page].item.page);
        await renderAllPages(pdfPage, preview.pages[page].item.rotationAngle, page);
      }
    }
  }, [renderAllPages, pdfRef, preview.pages]);

  useEffect(() => {
    if (pageIndex === null) setCurrentPage(pageIndex);
    else setCurrentPage(pageIndex + 1);
  }, [pageIndex]);

  useEffect(() => {
    if (preview.isDocument) {
      renderNewDocumentPages();
    }
    if (currentPage === null) {
      renderPages();
    } else if (pdfRef?.numPages >= currentPage) {
      renderPage(currentPage, pdfRef);
    }
  }, [pdfRef, currentPage, renderPage, renderPages, preview, renderNewDocumentPages]);

  useEffect(() => {
    if (preview.isDocument) {
      const files = [...new Set(preview.pages.map(a => a.item.correlationId).flat())];
      Promise.all(
        files.map(async correlationId => {
          const { item } = preview.pages.find(a => a.item?.correlationId === correlationId);
          const file = preview.files.find(x => x.correlationId === item.correlationId);

          return { correlationId, pdfProxy: await pdfjsLib.getDocument(file.data).promise };
        })
      ).then(r => {
        setPdfRef(r.reduce((a, v) => ({ ...a, [v.correlationId]: v.pdfProxy }), {}));
      });

      return;
    }

    const loadingTask = pdfjsLib.getDocument(preview.file);
    loadingTask.promise.then(
      loadedPdf => {
        setPdfRef(loadedPdf);
      },
      function (reason) {
        console.error(reason);
      }
    );
  }, [preview]);

  return (
    <PdfViewerDialog
      open={open}
      onClose={() => {
        setOpen(false);
        setPreview(null);
      }}
      aria-labelledby='responsive-dialog-title'
      closeButtonProps={{ sx: { display: 'none' } }}
      fullWidth
      maxWidth={'md'}
      transitionDuration={0}
      title={
        <>
          <Typography fontWeight='bold' color='primary' variant='h6'>
            {getPreviewTitle(preview)}
          </Typography>
          <Grid container justifyContent='end' alignItems='center' gap={1}>
            <PreviewZoomComponent scale={scale} setScale={setScale} />
            {preview.canRotate && (
              <>
                <IconButton color='primary' size='small' variant='text' onClick={() => rotatePage(true)} aria-label='rotate-clockwise'>
                  <RotateRight />
                </IconButton>
                <IconButton
                  color='primary'
                  size='small'
                  variant='text'
                  onClick={() => rotatePage(false)}
                  aria-label='rotate-counterclockwise'
                >
                  <RotateLeft />
                </IconButton>
              </>
            )}
          </Grid>
        </>
      }
      content={
        preview && (
          <>
            <div ref={divRef}></div>
            <canvas ref={canvasRef}></canvas>
          </>
        )
      }
      actions={
        <BoxActionsWrapper forDialog>
          <Button
            onClick={() => {
              setOpen(false);
              setPreview(null);
            }}
          >
            {t('OfferEdit.Documents.Preview.Close')}
          </Button>
        </BoxActionsWrapper>
      }
    />
  );
}

PdfPageViewer.propTypes = {
  url: PropTypes.string,
  pageIndex: PropTypes.number,
  viewportScale: PropTypes.number,
  preview: PropTypes.object,
  setPreview: PropTypes.func.isRequired,
  setPages: PropTypes.func.isRequired,
  pages: PropTypes.array.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
};
